<template>
    <v-container fluid class="multa-page pa-8 pt-12">
        <div class="multa-header">
            <div>
                <h1 class="multa-title">Gestão de Multas</h1>
                <p class="multa-subtitle">
                    Gerencie infrações da frota, responsáveis, prazos e situação de pagamento.
                </p>
            </div>
            <v-btn class="multa-new-btn" depressed @click="abrirNovaMulta">
                <v-icon left size="18">mdi-plus</v-icon>
                Nova multa
            </v-btn>
        </div>

        <v-row class="mt-6" dense>
            <v-col cols="12" md="3">
                <v-card class="multa-stat" outlined>
                    <div class="multa-stat__content">
                        <div>
                            <div class="multa-stat__label">Total de multas</div>
                            <div class="multa-stat__value">{{ multas.length }}</div>
                        </div>
                        <div class="multa-stat__icon multa-stat__icon--primary">
                            <v-icon color="#ef4444" size="20">mdi-alert-circle-outline</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <v-card class="multa-stat" outlined>
                    <div class="multa-stat__content">
                        <div>
                            <div class="multa-stat__label">Pendentes</div>
                            <div class="multa-stat__value multa-stat__value--warning">{{ totalPendentes }}</div>
                        </div>
                        <div class="multa-stat__icon multa-stat__icon--warning">
                            <v-icon color="#f59e0b" size="20">mdi-timer-sand</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <v-card class="multa-stat" outlined>
                    <div class="multa-stat__content">
                        <div>
                            <div class="multa-stat__label">Pagas</div>
                            <div class="multa-stat__value multa-stat__value--accent">{{ totalPagas }}</div>
                        </div>
                        <div class="multa-stat__icon multa-stat__icon--accent">
                            <v-icon color="#10b981" size="20">mdi-check-circle-outline</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <v-card class="multa-stat" outlined>
                    <div class="multa-stat__content">
                        <div>
                            <div class="multa-stat__label">Valor total</div>
                            <div class="multa-stat__value">{{ formatCurrency(valorTotal) }}</div>
                        </div>
                        <div class="multa-stat__icon multa-stat__icon--danger">
                            <v-icon color="#dc2626" size="20">mdi-cash-remove</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-card class="multa-table-card mt-6" outlined>
            <div class="multa-table-card__toolbar">
                <v-menu offset-y left :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            class="multa-icon-btn"
                            v-bind="attrs"
                            v-on="on"
                            title="Colunas"
                            aria-label="Colunas"
                        >
                            <v-icon size="18">mdi-cog-outline</v-icon>
                        </v-btn>
                    </template>

                    <v-card class="multa-colunas-card" outlined>
                        <v-card-title class="multa-colunas-card__title">
                            <span>Colunas visíveis</span>
                            <v-spacer />
                            <v-btn text small color="#10b981" @click="restaurarColunasPadrao">
                                Padrão
                            </v-btn>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pt-4 pb-3">
                            <div class="multa-colunas-card__hint">
                                Escolha quais colunas devem aparecer na tabela.
                            </div>
                            <v-checkbox
                                v-for="column in columnOptions"
                                :key="column.value"
                                :input-value="isColumnVisible(column.value)"
                                :label="column.text"
                                hide-details
                                dense
                                class="multa-colunas-card__checkbox mt-0"
                                @change="toggleVisibleColumn(column.value)"
                            />
                        </v-card-text>
                    </v-card>
                </v-menu>

                <v-badge
                    :value="hasActiveFilters"
                    color="red"
                    dot
                    overlap
                >
                    <v-btn
                        icon
                        class="multa-icon-btn"
                        title="Filtros"
                        aria-label="Filtros"
                        @click="abrirFiltros"
                    >
                        <v-icon size="18">mdi-filter-outline</v-icon>
                    </v-btn>
                </v-badge>
            </div>
            <v-data-table
                class="multa-table"
                :headers="visibleHeaders"
                :items="multasFiltradas"
                :loading="loading"
                loading-text="Carregando multas..."
                no-data-text="Nenhuma multa cadastrada"
                :items-per-page="8"
                :footer-props="footerProps"
            >
                <template v-slot:[`item.veiculo`]='{ item }'>
                    <div class="multa-veiculo-cell">
                        <strong>{{ item.veiculo_placa || '-' }}</strong>
                        <span>{{ item.motorista_nome || 'Sem motorista vinculado' }}</span>
                    </div>
                </template>

                <template v-slot:[`item.data_infracao`]='{ item }'>
                    <span>{{ formatDate(item.data_infracao) }}</span>
                </template>

                <template v-slot:[`item.gravidade`]='{ item }'>
                    <span class="multa-gravidade" :class="getGravidadeClass(item.gravidade)">
                        {{ item.gravidade || '-' }}
                    </span>
                </template>

                <template v-slot:[`item.status`]='{ item }'>
                    <span class="multa-status" :class="getStatusClass(item.status)">
                        {{ item.status || '-' }}
                    </span>
                </template>

                <template v-slot:[`item.valor`]='{ item }'>
                    <strong>{{ formatCurrency(item.valor) }}</strong>
                </template>

                <template v-slot:[`item.acoes`]='{ item }'>
                    <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small class="multa-actions" v-bind="attrs" v-on="on">
                                <v-icon color="#10b981">mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense class="multa-actions__menu">
                            <v-list-item @click="abrirDialogDetalhes(item)">
                                <v-list-item-icon>
                                    <v-icon color="#2563eb" size="18">mdi-eye-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Detalhes</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="editarMulta(item)">
                                <v-list-item-icon>
                                    <v-icon color="#10b981" size="18">mdi-pencil-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Editar</v-list-item-title>
                            </v-list-item>
                            <v-divider />
                            <v-list-item @click="abrirDialogExcluir(item)">
                                <v-list-item-icon>
                                    <v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Excluir</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>                   
            </v-data-table>
        </v-card>

        <v-dialog v-model="filterDialogOpen" max-width="480" persistent>
            <v-card class="multa-dialog" outlined>
                <v-card-title class="multa-dialog__title">
                    <span>Filtrar multas</span>
                    <v-spacer />
                    <v-btn icon @click="filterDialogOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-0">
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="filterForm.status"
                                :items="statusFiltroOptions"
                                label="Status"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="filterForm.veiculo_id"
                                :items="veiculoFilterOptions"
                                item-text="label"
                                item-value="value"
                                label="Veículo"
                                outlined
                                dense
                                clearable
                                no-data-text="Nenhum veículo encontrado"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="filterForm.motorista_id"
                                :items="motoristaFilterOptions"
                                item-text="label"
                                item-value="value"
                                label="Motorista"
                                outlined
                                dense
                                clearable
                                no-data-text="Nenhum motorista encontrado"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="filterForm.gravidade"
                                :items="gravidadeOptions"
                                label="Gravidade"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="filterForm.data_de"
                                label="Data de"
                                type="date"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="filterForm.data_ate"
                                label="Data até"
                                type="date"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-btn text @click="limparFiltros">Limpar</v-btn>
                    <v-spacer />
                    <v-btn text @click="filterDialogOpen = false">Cancelar</v-btn>
                    <v-btn class="multa-new-btn" depressed @click="aplicarFiltros">
                        Filtrar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="detalhesDialogOpen" max-width="600" persistent>
            <v-card class="multa-dialog" outlined>
                <v-card-title class="multa-dialog__title">
                    <span>Detalhes da Multa</span>
                    <v-spacer />
                    <v-btn icon @click="detalhesDialogOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-0">
                    <v-simple-table dense>
                        <tbody>
                            <tr><td><b>Veículo</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.veiculo_placa || '-' }}</td></tr>
                            <tr><td><b>Motorista</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.motorista_nome || '-' }}</td></tr>
                            <tr><td><b>Descrição</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.descricao || '-' }}</td></tr>
                            <tr><td><b>Status</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.status || '-' }}</td></tr>
                            <tr><td><b>Gravidade</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.gravidade || '-' }}</td></tr>
                            <tr><td><b>Número do auto</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.numero_auto || '-' }}</td></tr>
                            <tr><td><b>Órgão autuador</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.orgao_autuador || '-' }}</td></tr>
                            <tr><td><b>Valor</b></td><td>{{ formatCurrency(detalhesSelecionado && detalhesSelecionado.valor) }}</td></tr>
                            <tr><td><b>Pontos</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.pontos || '-' }}</td></tr>
                            <tr><td><b>Data da infração</b></td><td>{{ formatDate(detalhesSelecionado && detalhesSelecionado.data_infracao) }}</td></tr>
                            <tr><td><b>Data de vencimento</b></td><td>{{ formatDate(detalhesSelecionado && detalhesSelecionado.data_vencimento) }}</td></tr>
                            <tr><td><b>Local da infração</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.local_infracao || '-' }}</td></tr>
                            <tr><td><b>Observações</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.observacoes || '-' }}</td></tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer />
                    <v-btn text @click="detalhesDialogOpen = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogOpen" max-width="820" persistent>
            <v-card class="multa-dialog" outlined>
                <v-card-title class="multa-dialog__title">
                    <span>{{ dialogTitle }}</span>
                    <v-spacer />
                    <v-btn icon :disabled="saving" @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-0">
                    <v-form ref="multaForm" v-model="formValid" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="form.veiculo_id"
                                    :items="veiculosOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Veículo"
                                    outlined
                                    dense
                                    :rules="rules.veiculo_id"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="form.motorista_id"
                                    :items="motoristasOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Motorista"
                                    outlined
                                    dense
                                    clearable
                                />
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model.trim="form.descricao"
                                    label="Descrição"
                                    outlined
                                    dense
                                    :rules="rules.descricao"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="form.status"
                                    :items="statusOptions"
                                    label="Status"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model.trim="form.numero_auto"
                                    label="Número do auto"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model.trim="form.orgao_autuador"
                                    label="Órgão autuador"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="form.gravidade"
                                    :items="gravidadeOptions"
                                    label="Gravidade"
                                    outlined
                                    dense
                                    clearable
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="form.valor"
                                    label="Valor"
                                    outlined
                                    dense
                                    prefix="R$"
                                    :rules="rules.valor"
                                    @input="onCurrencyInput('valor')"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="form.pontos"
                                    label="Pontos"
                                    outlined
                                    dense
                                    inputmode="numeric"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.data_infracao"
                                    label="Data da infração"
                                    type="date"
                                    outlined
                                    dense
                                    :rules="rules.data_infracao"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.data_vencimento"
                                    label="Data de vencimento"
                                    type="date"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model.trim="form.local_infracao"
                                    label="Local da infração"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model.trim="form.observacoes"
                                    label="Observações"
                                    outlined
                                    dense
                                    rows="3"
                                    auto-grow
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer />
                    <v-btn text :disabled="saving" @click="closeDialog">Cancelar</v-btn>
                    <v-btn class="multa-new-btn" depressed :loading="saving" :disabled="saving" @click="saveMulta">
                        Salvar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
            <v-card class="multa-dialog" outlined>
                <v-card-title class="multa-dialog__title">
                    <span>Excluir multa</span>
                    <v-spacer />
                    <v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-2">
                    Tem certeza que deseja excluir <strong>{{ registroExclusao.descricao || '-' }}</strong>?
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
                    <v-btn class="multa-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusao">
                        Excluir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import multaVeiculoServices from './multaVeiculoServices';
import veiculoServices from '../veiculos/veiculoServices';
import motoristaServices from '../motoristas/motoristaServices';

function getInitialForm() {
    return {
        id: null,
        veiculo_id: null,
        motorista_id: null,
        descricao: '',
        numero_auto: '',
        orgao_autuador: '',
        gravidade: null,
        status: 'Pendente',
        local_infracao: '',
        pontos: '',
        valor: '',
        data_infracao: new Date().toISOString().slice(0, 10),
        data_vencimento: '',
        observacoes: '',
    };
}

const MULTA_VISIBLE_COLUMNS_STORAGE_KEY = 'machado_web_transportadoras.multas.visible_columns';
const AVAILABLE_HEADERS = [
    { text: 'Veículo / Motorista', value: 'veiculo', sortable: false },
    { text: 'Descrição', value: 'descricao', sortable: false },
    { text: 'Data', value: 'data_infracao', sortable: false },
    { text: 'Gravidade', value: 'gravidade', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Valor', value: 'valor', sortable: false },
    { text: 'Ações', value: 'acoes', sortable: false, align: 'center' },
];
const DEFAULT_VISIBLE_COLUMNS = AVAILABLE_HEADERS
    .filter((header) => header.value !== 'acoes')
    .map((header) => header.value);

export default {
    name: 'ListagemMultasVeiculos',

    data() {
        return {
            loading: false,
            saving: false,
            deleting: false,
            dialogOpen: false,
            deleteDialogOpen: false,
            detalhesDialogOpen: false,
            detalhesSelecionado: null,
            formValid: false,
            multas: [],
            veiculos: [],
            motoristas: [],
            registroExclusao: {},
            form: getInitialForm(),
            statusOptions: ['Pendente', 'Em recurso', 'Paga', 'Cancelada'],
            gravidadeOptions: ['Leve', 'Média', 'Grave', 'Gravíssima'],
            filterDialogOpen: false,
            filterForm: {
                status: null,
                veiculo_id: null,
                motorista_id: null,
                gravidade: null,
                data_de: '',
                data_ate: '',
            },
            activeFilters: {
                status: null,
                veiculo_id: null,
                motorista_id: null,
                gravidade: null,
                data_de: '',
                data_ate: '',
            },
            visibleColumnValues: [...DEFAULT_VISIBLE_COLUMNS],
            footerProps: {
                itemsPerPageText: 'Itens por Página',
                itemsPerPageOptions: [10, 15, 20],
                pageText: '{0}-{1} de {2}'
            },
            rules: {
                veiculo_id: [(v) => Boolean(v) || 'Selecione o veículo'],
                descricao: [(v) => Boolean(String(v || '').trim()) || 'Informe a descrição'],
                valor: [(v) => Boolean(String(v || '').trim()) || 'Informe o valor'],
                data_infracao: [(v) => Boolean(v) || 'Informe a data da infração'],
            },
        };
    },

    computed: {
        dialogTitle() {
            return this.form.id ? 'Editar multa' : 'Nova multa';
        },

        columnOptions() {
            return AVAILABLE_HEADERS.filter((header) => header.value !== 'acoes');
        },

        visibleHeaders() {
            return AVAILABLE_HEADERS.filter(
                (header) => header.value === 'acoes' || this.visibleColumnValues.includes(header.value),
            );
        },

        veiculoFilterOptions() {
            return this.veiculos.map((item) => ({
                label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
                value: item.id,
            }));
        },

        motoristaFilterOptions() {
            return this.motoristas.map((item) => ({
                label: item.nome,
                value: item.id,
            }));
        },

        statusFiltroOptions() {
            return this.statusOptions;
        },

        hasActiveFilters() {
            const f = this.activeFilters;
            return Boolean(f.status || f.veiculo_id || f.motorista_id || f.gravidade || f.data_de || f.data_ate);
        },

        multasFiltradas() {
            const f = this.activeFilters;
            return this.multas.filter((item) => {
                if (f.status && item.status !== f.status) return false;
                if (f.veiculo_id && item.veiculo_id !== f.veiculo_id) return false;
                if (f.motorista_id && item.motorista_id !== f.motorista_id) return false;
                if (f.gravidade && item.gravidade !== f.gravidade) return false;
                const raw = item.data_infracao ? String(item.data_infracao).slice(0, 10) : '';
                if (f.data_de && (!raw || raw < f.data_de)) return false;
                if (f.data_ate && (!raw || raw > f.data_ate)) return false;
                return true;
            });
        },

        veiculosOptions() {
            return this.veiculos.map((item) => ({
                label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
                value: item.id,
            }));
        },

        motoristasOptions() {
            return this.motoristas.map((item) => ({
                label: item.nome,
                value: item.id,
            }));
        },

        totalPendentes() {
            return this.multas.filter((item) => String(item.status || '') === 'Pendente').length;
        },

        totalPagas() {
            return this.multas.filter((item) => String(item.status || '') === 'Paga').length;
        },

        valorTotal() {
            return this.multas.reduce((acc, item) => acc + (Number(item.valor) || 0), 0);
        },
    },

    methods: {
        normalizeVisibleColumns(values) {
            const allowedValues = AVAILABLE_HEADERS
                .filter((header) => header.value !== 'acoes')
                .map((header) => header.value);
            const selectedValues = new Set(Array.isArray(values) ? values : []);
            const normalizedValues = allowedValues.filter((value) => selectedValues.has(value));
            return normalizedValues.length ? normalizedValues : [...DEFAULT_VISIBLE_COLUMNS];
        },

        loadVisibleColumnsPreference() {
            try {
                const storedColumns = localStorage.getItem(MULTA_VISIBLE_COLUMNS_STORAGE_KEY);
                if (!storedColumns) {
                    return;
                }
                this.visibleColumnValues = this.normalizeVisibleColumns(JSON.parse(storedColumns));
            } catch (error) {
                this.visibleColumnValues = [...DEFAULT_VISIBLE_COLUMNS];
            }
        },

        persistVisibleColumnsPreference() {
            try {
                localStorage.setItem(
                    MULTA_VISIBLE_COLUMNS_STORAGE_KEY,
                    JSON.stringify(this.visibleColumnValues),
                );
            } catch (error) {
                // Ignora falhas de armazenamento local sem interromper a tela.
            }
        },

        isColumnVisible(columnValue) {
            return this.visibleColumnValues.includes(columnValue);
        },

        toggleVisibleColumn(columnValue) {
            const isVisible = this.isColumnVisible(columnValue);

            if (isVisible && this.visibleColumnValues.length === 1) {
                this.$toasted?.show('Mantenha ao menos uma coluna visível na tabela.');
                return;
            }

            const nextValues = isVisible
                ? this.visibleColumnValues.filter((value) => value !== columnValue)
                : [...this.visibleColumnValues, columnValue];

            this.visibleColumnValues = this.normalizeVisibleColumns(nextValues);
            this.persistVisibleColumnsPreference();
        },

        restaurarColunasPadrao() {
            this.visibleColumnValues = [...DEFAULT_VISIBLE_COLUMNS];
            this.persistVisibleColumnsPreference();
            this.$toasted?.show('Colunas restauradas para o padrão.');
        },

        abrirFiltros() {
            this.filterForm = { ...this.activeFilters };
            this.filterDialogOpen = true;
        },

        aplicarFiltros() {
            this.activeFilters = { ...this.filterForm };
            this.filterDialogOpen = false;
        },

        limparFiltros() {
            const empty = { status: null, veiculo_id: null, motorista_id: null, gravidade: null, data_de: '', data_ate: '' };
            this.filterForm = { ...empty };
            this.activeFilters = { ...empty };
            this.filterDialogOpen = false;
        },

        async carregarDados() {
            this.loading = true;
            try {
                const [multasResponse, veiculosResponse, motoristasResponse] = await Promise.all([
                    multaVeiculoServices.listar(),
                    veiculoServices.listar(),
                    motoristaServices.listar(),
                ]);

                this.multas = Array.isArray(multasResponse?.data) ? multasResponse.data : [];
                this.veiculos = Array.isArray(veiculosResponse?.data) ? veiculosResponse.data : [];
                this.motoristas = Array.isArray(motoristasResponse?.data) ? motoristasResponse.data : [];
            } catch (error) {
                this.$toasted?.show(error?.response?.data?.error || 'Erro ao carregar multas.');
            } finally {
                this.loading = false;
            }
        },

        abrirDialogDetalhes(item) {
            this.detalhesSelecionado = item;
            this.detalhesDialogOpen = true;
        },

        abrirNovaMulta() {
            this.form = getInitialForm();
            this.dialogOpen = true;
            this.formValid = false;
            this.$nextTick(() => this.$refs?.multaForm?.resetValidation?.());
        },

        editarMulta(item) {
            this.form = {
                id: item.id,
                veiculo_id: item.veiculo_id,
                motorista_id: item.motorista_id,
                descricao: item.descricao || '',
                numero_auto: item.numero_auto || '',
                orgao_autuador: item.orgao_autuador || '',
                gravidade: item.gravidade || null,
                status: item.status || 'Pendente',
                local_infracao: item.local_infracao || '',
                pontos: item.pontos === null || item.pontos === undefined ? '' : String(item.pontos),
                valor: item.valor === null || item.valor === undefined ? '' : Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                data_infracao: this.formatDateInput(item.data_infracao),
                data_vencimento: this.formatDateInput(item.data_vencimento),
                observacoes: item.observacoes || '',
            };

            this.dialogOpen = true;
            this.formValid = false;
            this.$nextTick(() => this.$refs?.multaForm?.resetValidation?.());
        },

        formatDateInput(value) {
            if (!value) return '';
            // Se já está no formato yyyy-MM-dd
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            // Se está no formato yyyy-MM-ddTHH:mm:ss ou yyyy-MM-dd HH:mm:ss
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
            // Se está no formato dd/MM/yyyy
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
                const [d, m, y] = value.split('/');
                return `${y}-${m}-${d}`;
            }
            // Tenta converter para Date
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        closeDialog() {
            this.dialogOpen = false;
            this.form = getInitialForm();
        },

        abrirDialogExcluir(item) {
            this.registroExclusao = item || {};
            this.deleteDialogOpen = true;
        },

        async confirmarExclusao() {
            if (!this.registroExclusao?.id) {
                return;
            }

            this.deleting = true;
            try {
                await multaVeiculoServices.excluir(this.registroExclusao.id);
                this.$toasted?.show('Multa excluída com sucesso.');
                this.deleteDialogOpen = false;
                this.registroExclusao = {};
                await this.carregarDados();
            } catch (error) {
                this.$toasted?.show(error?.response?.data?.error || 'Erro ao excluir multa.');
            } finally {
                this.deleting = false;
            }
        },

           async saveMulta() {
               const isValid = this.$refs?.multaForm?.validate?.();
               if (!isValid) {
                   return;
               }

               const payload = {
                   veiculo_id: this.form.veiculo_id,
                   motorista_id: this.form.motorista_id || null,
                   descricao: this.form.descricao,
                   numero_auto: this.form.numero_auto || null,
                   orgao_autuador: this.form.orgao_autuador || null,
                   gravidade: this.form.gravidade || null,
                   status: this.form.status,
                   local_infracao: this.form.local_infracao || null,
                   pontos: this.form.pontos === '' ? null : this.form.pontos,
                   valor: this.parseCurrency(this.form.valor),
                   data_infracao: this.form.data_infracao,
                   data_vencimento: this.form.data_vencimento || null,
                   observacoes: this.form.observacoes || null,
               };

               this.saving = true;
               try {
                   if (this.form.id) {
                       await multaVeiculoServices.atualizar(this.form.id, payload);
                       this.$toasted?.show('Multa atualizada com sucesso.');
                   } else {
                       await multaVeiculoServices.criar(payload);
                       this.$toasted?.show('Multa criada com sucesso.');
                   }

                   this.closeDialog();
                   await this.carregarDados();
               } catch (error) {
                   this.$toasted?.show(error?.response?.data?.error || 'Erro ao salvar multa.');
               } finally {
                   this.saving = false;
               }
           },
       formatCurrencyInput(value) {
           const digitsOnly = String(value || '').replace(/\D/g, '');
           if (!digitsOnly) {
               return '';
           }
           const centsValue = Number(digitsOnly) / 100;
           return centsValue.toLocaleString('pt-BR', {
               minimumFractionDigits: 2,
               maximumFractionDigits: 2,
           });
       },

       parseCurrency(value) {
           if (!value) {
               return 0;
           }
           const normalized = String(value)
               .replace(/\./g, '')
               .replace(',', '.');
           const parsed = Number(normalized);
           return Number.isFinite(parsed) ? parsed : 0;
       },

       onCurrencyInput(fieldName) {
           this.form[fieldName] = this.formatCurrencyInput(this.form[fieldName]);
       },

        formatCurrency(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }

            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return String(value);
            }

            return numericValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            });
        },

        formatDate(value) {
            if (!value) {
                return '-';
            }

            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
                return String(value);
            }

            return parsed.toLocaleDateString('pt-BR');
        },

        getGravidadeClass(gravidade) {
            const valor = String(gravidade || '').toLowerCase();
            if (valor === 'leve') return 'is-leve';
            if (valor === 'média') return 'is-media';
            if (valor === 'grave') return 'is-grave';
            if (valor === 'gravíssima') return 'is-gravissima';
            return '';
        },

        getStatusClass(status) {
            const valor = String(status || '').toLowerCase();
            if (valor === 'paga') return 'is-success';
            if (valor === 'cancelada') return 'is-danger';
            if (valor === 'em recurso') return 'is-info';
            return 'is-warning';
        },
    },

    mounted() {
        this.loadVisibleColumnsPreference();
        this.carregarDados();
    },
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.multa-page {
    font-family: "Inter", sans-serif;
}

.multa-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.multa-title {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    color: #10b981;
    line-height: 1.2;
}

.multa-subtitle {
    margin: 6px 0 0;
    max-width: 640px;
    color: #7a8ca6;
    font-size: 13px;
}

.multa-new-btn {
    background-color: #10b981 !important;
    color: #ffffff !important;
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.01em;
    padding: 10px 16px;
}

.multa-stat {
    border-radius: 16px !important;
    border-color: #e6ebf1 !important;
}

.multa-table-card__toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    padding: 0 12px;
    margin-top: -20px;
}

.multa-icon-btn {
    width: 38px;
    height: 38px;
    min-width: 38px !important;
    border-radius: 12px !important;
    background-color: #ffffff !important;
    color: #10b981 !important;
    border: 2px solid #10b981 !important;
}

.multa-colunas-card {
    border-radius: 12px !important;
    border-color: #e6ebf1 !important;
    min-width: 240px;
}

.multa-colunas-card__title {
    font-size: 14px !important;
    font-weight: 700;
    color: #1f2a44;
    padding: 12px 16px;
}

.multa-colunas-card__hint {
    font-size: 12px;
    color: #7a8ca6;
    margin-bottom: 8px;
}

.multa-colunas-card__checkbox {
    font-size: 13px;
    color: #1f2a44;
}

.multa-table-card {
    border-radius: 16px !important;
    border-color: #e6ebf1 !important;
}

.multa-dialog {
    border-radius: 16px !important;
    border-color: #e6ebf1 !important;
}

.multa-stat__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px;
    gap: 12px;
}

.multa-stat__label {
    font-size: 12px;
    font-weight: 600;
    color: #7a8ca6;
}

.multa-stat__value {
    margin-top: 6px;
    font-size: 28px;
    font-weight: 800;
    color: #1f2a44;
}

.multa-stat__value--warning {
    color: #b45309;
}

.multa-stat__value--accent {
    color: #0f9f6e;
}

.multa-stat__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    flex-shrink: 0;
}

.multa-stat__icon--primary {
    background: rgba(239, 68, 68, 0.12);
}

.multa-stat__icon--warning {
    background: rgba(245, 158, 11, 0.16);
}

.multa-stat__icon--accent {
    background: rgba(16, 185, 129, 0.12);
}

.multa-stat__icon--danger {
    background: rgba(220, 38, 38, 0.12);
}

.multa-table {
    border-radius: 16px;
}

.multa-table ::v-deep th {
    color: #7a8ca6 !important;
    font-weight: 800 !important;
    font-size: 11px !important;
    letter-spacing: 0.08em;
}

.multa-table ::v-deep td {
    color: #1f2a44;
    font-size: 13px;
}

.multa-table ::v-deep .v-data-table__wrapper {
    border-radius: 16px;
}

.multa-veiculo-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.multa-veiculo-cell span {
    font-size: 12px;
    color: #7a8ca6;
}

.multa-status {
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.multa-status.is-success {
    background: rgba(16, 185, 129, 0.12);
    color: #0f9f6e;
}

.multa-status.is-danger {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.multa-status.is-info {
    background: rgba(37, 99, 235, 0.12);
    color: #2563eb;
}

.multa-status.is-warning {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.multa-gravidade {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.multa-gravidade.is-leve {
    background: rgba(16, 185, 129, 0.12);
    color: #0f9f6e;
}

.multa-gravidade.is-media {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.multa-gravidade.is-grave {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.multa-gravidade.is-gravissima {
    background: rgba(127, 29, 29, 0.12);
    color: #7f1d1d;
}

.multa-actions {
    opacity: 0.9;
}

.multa-actions__menu {
    min-width: 180px;
}

.multa-dialog__title {
    color: #1f2a44;
    font-weight: 800;
}

.multa-delete-btn {
    background-color: #ef4444 !important;
    color: #ffffff !important;
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.01em;
    padding: 10px 16px;
}
</style>